// useAutoDiagnose — B5 桥5 + B5.2-cron · 浏览器在场时的即时自动诊断补充。
//
// 双路径架构 (B5.2-cron 后):
//   - 后端 cron (主路径 · packages/ingest/src/diagnose/cron-diagnose.ts):
//     浏览器关着时，每 10min tick 检查 opt-in 用户，按 intervalHours + stale
//     判定 + 服务端聚合 stats + 扣点 + 写 diagnosisPending。用户不在场也能诊断。
//   - 本 hook (补充路径 · 浏览器开着时): 用户在学习、刚改完错题想立刻看诊断，
//     不想等下一个 cron tick。挂载检查 + 10min 节拍，复用同一 pending 队列。
//   两者互补：cron 覆盖"离开时"，本 hook 覆盖"在场时即时"。pending 队列的
//   LWW merge (verdict.diagnosedAt) 保证不会重复写——cron 写的 pending 通过
//   hydration 同步到前端 IDB，本 hook 看到 pending 就跳过 (步骤 2)。
//
// 产品契约（与用户确认的方案一致）：
//   - AI 诊断（质量最高），本地统计作 prompt 增强（B4 已闭环，本 hook 直接复用）。
//   - 产出的 verdict 进 progress.diagnosisPending 待审队列（不直接写 diagnosis），
//     用户 accept 才生效——保留铁律 3（用户显式触发）精神。
//   - 默认关 + opt-in（account-slice.autoDiagnoseEnabled · 云化 · cron 也读这个）。
//   - 余额不足静默跳过（不弹 toast、不扣点——扣点是后端 Gate 5 的职责）。
//
// 调度逻辑（checkAndRun，挂载时跑一次 + 每 CHECK_INTERVAL_MS 跑一次）：
//   1. 总开关关 → 直接返回。
//   2. 已有 pending（用户还没审上一次）→ 不重复跑（避免堆积）。
//   3. 正在跑（autoDiagnoseRunning）→ 不重入。
//   4. 阈值检查：距上次"成功诊断"（progress.diagnosis.verdict.diagnosedAt）
//      或"上次尝试"（lastAutoDiagnoseAttemptAt，含失败）取较新者，未超 interval → 跳过。
//   5. 数据充分性：unresolved 错题 < MIN_UNRESOLVED → 不值得烧点诊断。
//   6. 余额检查：balance < DIAGNOSE_EST_POINTS → 静默跳过。
//   7. 调 postDiagnose → 成功写 pending + 瞬时通知；失败记 attempt 防抖。
//
// 挂载点：CourseShell（课程级壳，已有 regen 轮询 hook 的先例）。

import { useEffect, useRef } from 'react';
import { useStore } from '../store/index.js';
import { useStoreCourseData } from './use-store-course.js';
import { wrongAnswerStats } from '../stats.js';
import { postDiagnose } from '../../api/diagnose.js';
import { updateProgress } from '../../storage/idb.js';
import { useNotify } from './use-notify.js';
import { useT } from '../../i18n/use-t.js';
import { ESTIMATED_POINTS } from './use-preflight-credits.js';
import type { CourseProgress, DiagnosisPending } from '@examforge/shared/schemas';

/** 页面停留期间的节拍间隔。不必等于用户设定的诊断间隔——这只是"多久检查
 *  一次是否该诊断"，实际诊断频率由 diagnosePreferences.autoDiagnoseIntervalHours
 *  + 阈值检查共同决定。10 分钟足够细粒度，又不至于频繁空跑检查。 */
const CHECK_INTERVAL_MS = 10 * 60 * 1000;

/** 数据充分性阈值：未解决错题少于此数不诊断（避免空跑烧点）。
 *  3 是保守值——太少错题时 LLM 诊断信息量低，不值得 ~3 点成本。 */
const MIN_UNRESOLVED = 3;

/**
 * 后台自动诊断调度器。在 CourseShell 挂载时启动，卸载时清理定时器。
 *
 * 注意：此 hook 必须在 useStoreCourseData() 可用的上下文里调用（即课程子路由内）。
 * CourseShell 已经保证了这一点。
 */
export function useAutoDiagnose(): void {
  const { draft, pkg, progress } = useStoreCourseData();
  const t = useT();
  const notify = useNotify();

  // Diagnose-slice 状态（调度控制态）。
  const autoDiagnoseEnabled = useStore((s) => s.diagnosePreferences.autoDiagnoseEnabled);
  const intervalHours = useStore((s) => s.diagnosePreferences.autoDiagnoseIntervalHours);
  const autoDiagnoseRunning = useStore((s) => s.autoDiagnoseRunning);
  const lastAttemptAt = useStore((s) => s.lastAutoDiagnoseAttemptAt);
  const setAutoDiagnoseRunning = useStore((s) => s.setAutoDiagnoseRunning);
  const recordAutoDiagnoseAttempt = useStore((s) => s.recordAutoDiagnoseAttempt);

  // Account-slice：余额（自动跑的静默门）。
  const balance = useStore((s) => s.balance);

  // 防止 useEffect 重复触发 checkAndRun 的 in-flight 标记。useEffect 的依赖
  // 数组里有 progress，progress 每次刷新引用变化都会重跑 effect——但 in-flight
  // 的诊断不该被打断。用 ref 守护。
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!autoDiagnoseEnabled) return;

    let cancelled = false;

    const checkAndRun = async (): Promise<void> => {
      if (cancelled) return;
      // 重入守护：上一次 checkAndRun 还在跑（异步 postDiagnose 未归）。
      if (inFlightRef.current) return;
      // slice 层重入守护（跨 effect 生命周期）。
      if (autoDiagnoseRunning) return;
      // 已有待审 verdict：用户还没决定，不堆积新的。
      if (progress.diagnosisPending) return;

      // ── 阈值检查：距上次"成功"或"尝试"是否够 interval ──────────────────
      const lastSuccessMs = parseDiagnosedAtMs(progress.diagnosis?.verdict.diagnosedAt);
      const lastAttemptMs = lastAttemptAt;
      const referenceMs = Math.max(lastSuccessMs ?? 0, lastAttemptMs ?? 0);
      const thresholdMs = intervalHours * 3600_000;
      if (Date.now() - referenceMs < thresholdMs) return;

      // ── 数据充分性 ──────────────────────────────────────────────────────
      const stats = wrongAnswerStats(pkg, progress);
      if (stats.unresolved < MIN_UNRESOLVED) return;

      // ── 余额静默门 ──────────────────────────────────────────────────────
      // 匿名（balance === null）：后端 Gate 5 会 401，但自动诊断对匿名用户
      // 无意义（没登录就没必要后台烧点）——静默跳过。
      if (balance === null) return;
      if (balance < ESTIMATED_POINTS.diagnose) return;

      // ── 执行诊断 ────────────────────────────────────────────────────────
      inFlightRef.current = true;
      setAutoDiagnoseRunning(true);
      recordAutoDiagnoseAttempt();
      try {
        const result = await postDiagnose(stats);
        if (cancelled) return; // 组件已卸载，丢弃结果

        const pending: DiagnosisPending = {
          verdict: result.verdict,
          statsSnapshot: {
            total: stats.total,
            unresolved: stats.unresolved,
            resolved: stats.resolved,
          },
          queuedAt: new Date().toISOString(),
        };
        await writePending(draft.id, pending);
        if (cancelled) return;

        // 瞬时通知（非 persistent）：诊断完成，引导用户回 dashboard 审阅。
        // 不用 persistent——pending verdict 本身会常驻 dashboard 卡片，toast
        // 只负责"刚跑完"的即时提醒。系统级通知策略是独立 follow-up 话题。
        notify({
          level: 'info',
          source: 'system',
          title: t('diagnose.autoNotifTitle'),
          message: t('diagnose.autoNotifMessage'),
          persistent: false,
          action: { label: t('diagnose.autoNotifView'), to: `/course/${draft.id}` },
        });
      } catch {
        // 静默失败：不弹错误 toast（自动跑不该打扰用户）。
        // recordAutoDiagnoseAttempt 已记录时间戳，防抖会在 interval 内阻止重试。
        // 可观测性：后端 diagnose endpoint 的 error-feed 会捕获失败，不依赖前端 toast。
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[useAutoDiagnose] auto diagnose failed (silent)');
        }
      } finally {
        inFlightRef.current = false;
        if (!cancelled) setAutoDiagnoseRunning(false);
      }
    };

    // 挂载即检查（打开 App/课程时立刻判断是否该跑）。
    void checkAndRun();
    // 页面停留期间节拍。
    const timer = setInterval(() => { void checkAndRun(); }, CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // 依赖：autoDiagnoseEnabled / intervalHours / balance / autoDiagnoseRunning /
    // lastAttemptAt 是调度输入；progress.diagnosisPending 是去重输入。progress
    // 和 pkg 引用每次刷新都变，但 checkAndRun 内部用 ref 守护重入，且阈值检查
    // 用时间戳比较，不会因 progress 引用变化而重复跑。draft.id 稳定。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoDiagnoseEnabled,
    intervalHours,
    balance,
    autoDiagnoseRunning,
    lastAttemptAt,
    progress.diagnosisPending,
    draft.id,
  ]);
}

/** 把 verdict.diagnosedAt（ISO 字符串）解析为 ms epoch，无效/缺失返回 null。 */
function parseDiagnosedAtMs(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/** 把 pending verdict 写入 progress.diagnosisPending（IDB）。 */
async function writePending(courseId: string, pending: DiagnosisPending): Promise<void> {
  await updateProgress(courseId, (p: CourseProgress) => {
    p.diagnosisPending = pending;
  });
}
